import { useMemo } from 'react';

import { type ConversationAssistantConfig } from '@atlassian/conversation-assistant/types';

/**
 * Attribution string sent to the assistance service so chat traffic originating
 * from the Full Page Search "chat" surface can be distinguished from other Rovo
 * chat entry points (Home sidebar, Confluence editor, etc.).
 */
export const FPS_CHAT_IN_PRODUCT_EXPERIENCE = 'fps-chat';

export type UseFpsChatConfigArgs = {
	/** Tenant cloud id (bootstrap `cloudId`). Maps to `siteId`. */
	cloudId: string;
	/** Current user id (bootstrap `user.id`). */
	userId: string | undefined | null;
	/**
	 * Host product, e.g. 'confluence' | 'jira'. `AssistanceServiceProduct`
	 * accepts any string; we default to 'confluence' when the primary product is
	 * not yet known (bootstrap in-flight) to match the Confluence-first rollout.
	 */
	product: string | undefined;
};

/**
 * Assembles the {@link ConversationAssistantConfig} for the Full Page Search chat
 * surface entirely from values already present in the search-page bootstrap store.
 */
export const useFpsChatConfig = ({
	cloudId,
	userId,
	product,
}: UseFpsChatConfigArgs): ConversationAssistantConfig => {
	return useMemo(
		() => ({
			displaySubmitFlag: true,
			siteId: cloudId,
			userId: userId ?? undefined,
			product: product ?? 'confluence',
			assistanceServiceConfig: {
				baseUrl: '/gateway/api/assist',
				headers: { 'x-issynthetic-traffic': 'false' },
			},
			inProductExperience: FPS_CHAT_IN_PRODUCT_EXPERIENCE,
		}),
		[cloudId, userId, product],
	);
};
